import { trendsPointKey } from '../../../../api/core'
import { TrendsAdoptionCounts, TrendsChartPoint, TrendsGranularity } from './trendsSeries'

// Monthly bucketing shared by every Live Event Analytics chart, so the line charts
// and the detail-page bars can never disagree on what a month is.
//
// Every trends endpoint returns monthly points already. `toMonthlySeries` is the entry
// point: it passes a monthly series straight through and still buckets a daily one.
//
// On the in-progress month: the backend no longer returns it (every figure covers whole
// calendar months, see `trendsSelectedPeriod`), and it is still dropped here for every
// lens should an older response carry it. Plotting a month after a few days of data
// reads as a sudden drop (2026-09-07 review). The rule is the lens, not the granularity:
// see `keepPartialTail`.
//
// Months are UTC calendar months, the backend's: a local-time month would drop the
// period's last whole month for a viewer west of UTC on the evening of its last day.

export interface MonthlyBucket extends TrendsAdoptionCounts {
  // Calendar month in "YYYY-MM" form.
  month: string
  // The axis key of the last point that fell in the month, so a hover can still name
  // the day the value was measured on. For an already-monthly series this is the
  // month itself.
  date: string
  value: number
  // True when the point does not cover a whole calendar month (only the month in
  // progress can), so callers can caption it rather than let it read as a real
  // month-on-month dip.
  partial?: boolean
}

export type MonthlyBucketMode =
  // Last daily value of the month. Fits "as of the date" figures such as adoption.
  | 'last'
  // Mean of the month's daily values. Fits per-day figures such as concurrent event
  // instances and density, and is how the backend builds its own monthly points.
  | 'mean'

export interface BucketMonthlyOptions {
  mode?: MonthlyBucketMode
  // Drop the calendar month `today` falls in (and anything after it).
  excludeCurrentMonth?: boolean
  // Injectable clock for tests.
  today?: Date
}

// "YYYY-MM" of a date in UTC.
export function monthKeyOf(date: Date): string {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`
}

// A month is complete when its last calendar day is before `today`'s day.
export function isCompleteMonth(month: string, today: Date = new Date()): boolean {
  return month < monthKeyOf(today)
}

export function bucketMonthly(series: readonly TrendsChartPoint[] | undefined, options: BucketMonthlyOptions = {}): MonthlyBucket[] {
  const { mode = 'last', excludeCurrentMonth = true, today = new Date() } = options
  if (!series || series.length === 0) return []

  const buckets = new Map<string, { date: string; sum: number; count: number; last: number; counts: TrendsAdoptionCounts }>()
  for (const point of series) {
    const key = trendsPointKey(point)
    const month = key.slice(0, 7)
    // The counts describe the day they were measured on, so the month takes them from
    // its last day — averaging a denominator across a month would invent a figure the
    // backend never reported.
    const counts: TrendsAdoptionCounts = { distinctGamesCount: point.distinctGamesCount, trackedGamesCount: point.trackedGamesCount }
    const existing = buckets.get(month)
    if (existing) {
      existing.sum += point.value
      existing.count += 1
      // Series are sorted ascending, but guard against an out-of-order point.
      if (key >= existing.date) {
        existing.date = key
        existing.last = point.value
        existing.counts = counts
      }
    } else {
      buckets.set(month, { date: key, sum: point.value, count: 1, last: point.value, counts })
    }
  }

  return Array.from(buckets.entries())
    .filter(([month]) => !excludeCurrentMonth || isCompleteMonth(month, today))
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([month, bucket]) => ({
      month,
      date: bucket.date,
      value: mode === 'mean' ? bucket.sum / bucket.count : bucket.last,
      ...bucket.counts,
    }))
}

export interface ToMonthlySeriesOptions extends BucketMonthlyOptions {
  // Keep the in-progress trailing month.
  //
  // Charts leave this false: a half-finished month is not comparable to a full one.
  // Callers that still need the as-of-today value (e.g. tests) can
  // opt in; `excludeCurrentMonth` still wins if set explicitly.
  keepPartialTail?: boolean
}

// Monthly points as the charts want them, whichever granularity the endpoint speaks.
//
// A daily series is bucketed, an already-monthly one passed through in order, with the
// in-progress month (if any) flagged `partial`. Either way that month survives only under
// a lens that can read a partial month (see `keepPartialTail`), so a daily and a monthly
// chart agree on which months exist. Deciding this per granularity instead is what made
// the two disagree.
export function toMonthlySeries(
  series: readonly TrendsChartPoint[] | undefined,
  granularity: TrendsGranularity,
  options: ToMonthlySeriesOptions = {}
): MonthlyBucket[] {
  const { keepPartialTail = true, today = new Date(), excludeCurrentMonth, ...bucketOptions } = options
  if (!series || series.length === 0) return []
  if (granularity === 'daily') {
    return bucketMonthly(series, { ...bucketOptions, today, excludeCurrentMonth: excludeCurrentMonth ?? !keepPartialTail })
  }

  const sorted = [...series].sort((a, b) => {
    const left = trendsPointKey(a)
    const right = trendsPointKey(b)
    return left < right ? -1 : left > right ? 1 : 0
  })

  const buckets = sorted.map((point) => {
    const month = trendsPointKey(point)
    return {
      month,
      date: month,
      value: point.value,
      distinctGamesCount: point.distinctGamesCount,
      trackedGamesCount: point.trackedGamesCount,
      partial: !isCompleteMonth(month, today),
    }
  })

  // Never trim to nothing: one partial point still beats an empty chart.
  const tail = buckets[buckets.length - 1]
  if (!keepPartialTail && buckets.length > 1 && tail.partial && !isCompleteMonth(tail.month, today)) {
    return buckets.slice(0, -1)
  }
  return buckets
}

// Whether a lens keeps the in-progress month on the chart. Always false: every lens
// drops it so half-finished months are not plotted as comparable points.
export function keepsPartialTailForMetric(_metric: 'adoption' | 'density' | 'activeEvents'): boolean {
  return false
}

// The bucketing mode each metric lens reads best with, should a daily series arrive:
// adoption is an "as of the date" figure, density and concurrent instances are per-day
// figures, averaged per month the way the backend builds its own monthly points.
export function monthlyModeForMetric(metric: 'adoption' | 'density' | 'activeEvents'): MonthlyBucketMode {
  return metric === 'adoption' ? 'last' : 'mean'
}
